import { useState } from "react";
import api, { apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { PageHeader, ExportButtons, SearchBar, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, Pencil, Trash2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nom: "", contact: "", telephone: "", email: "", produits: "", note: "" };

export default function Suppliers() {
  const { isAdmin, can } = useAuth();
  const { data, loading, refetch } = useData("/suppliers");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [query, setQuery] = useState("");

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditId(s.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      toast.success(editId ? "Fournisseur modifié" : "Fournisseur ajouté");
      setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer ${s.nom} ?`)) return;
    await api.delete(`/suppliers/${s.id}`); toast.success("Supprimé"); refetch();
  };

  const cols = [
    { header: "Fournisseur", value: (r) => r.nom },
    { header: "Contact", value: (r) => r.contact || "" },
    { header: "Téléphone", value: (r) => r.telephone || "" },
    { header: "Email", value: (r) => r.email || "" },
    { header: "Produits", value: (r) => r.produits || "" },
  ];

  const q = query.trim().toLowerCase();
  const filtered = q
    ? data.filter((s) => [s.nom, s.contact, s.telephone, s.email, s.produits, s.note].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
    : data;

  return (
    <div>
      <PageHeader title="Fournisseurs" subtitle="Carnet de contacts des fournisseurs.">
        {can("fournisseurs", "export") && <ExportButtons onExcel={() => exportExcel("fournisseurs_tiramessi", cols, data)} onPDF={() => exportPDF("Fournisseurs TIRAMESSI", cols, data)} />}
        {can("fournisseurs", "create") && <button className="tm-btn" onClick={openNew} data-testid="add-supplier-btn"><Plus size={16} /> Nouveau fournisseur</button>}
      </PageHeader>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucun fournisseur." /> : (
        <>
          <SearchBar value={query} onChange={setQuery} placeholder="Rechercher un fournisseur, un produit, un contact..." testid="supplier-search" count={filtered.length} total={data.length} />
          {filtered.length === 0 ? <Empty text="Aucun résultat pour cette recherche." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="suppliers-grid">
          {filtered.map((s) => (
            <div key={s.id} className="tm-card p-5 tm-fade" data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-display text-xl">{s.nom}</p>
                  {s.contact && <p className="text-sm text-[var(--tm-muted)]">{s.contact}</p>}
                </div>
                {(can("fournisseurs", "edit") || can("fournisseurs", "delete")) && (
                  <div className="flex gap-2">
                    {can("fournisseurs", "edit") && <button onClick={() => openEdit(s)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)]"><Pencil size={15} /></button>}
                    {can("fournisseurs", "delete") && <button onClick={() => remove(s)} className="text-[var(--tm-muted)] hover:text-[#F44336]"><Trash2 size={15} /></button>}
                  </div>
                )}
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                {s.telephone && <p className="flex items-center gap-2 text-[var(--tm-muted)]"><Phone size={14} style={{ color: "var(--tm-gold)" }} /> {s.telephone}</p>}
                {s.email && <p className="flex items-center gap-2 text-[var(--tm-muted)]"><Mail size={14} style={{ color: "var(--tm-gold)" }} /> {s.email}</p>}
              </div>
              {s.produits && <p className="mt-3 text-sm" style={{ color: "var(--tm-beige)" }}>{s.produits}</p>}
              {s.note && <p className="mt-2 text-xs text-[var(--tm-muted)]">{s.note}</p>}
            </div>
          ))}
        </div>
          )}
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier le fournisseur" : "Nouveau fournisseur"} testid="supplier-modal">
        <form onSubmit={save}>
          <Field label="Nom"><input className="tm-input" required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="supplier-nom" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact"><input className="tm-input" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></Field>
            <Field label="Téléphone"><input className="tm-input" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="supplier-tel" /></Field>
          </div>
          <Field label="Email"><input type="email" className="tm-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Produits fournis"><input className="tm-input" value={form.produits} onChange={(e) => setForm({ ...form, produits: e.target.value })} placeholder="Mascarpone, crème liquide..." /></Field>
          <Field label="Note"><input className="tm-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
          <button type="submit" className="tm-btn w-full mt-2" data-testid="supplier-save">Enregistrer</button>
        </form>
      </Modal>
    </div>
  );
}
